/**
 * Shinami Gas Station integration for sponsored transactions.
 * Users transact without needing SUI tokens — gas is paid from the Shinami fund.
 *
 * Requests are proxied through the Vercel function at /api/zklogin/sponsor
 * to avoid CORS issues (Shinami Gas Station does not support browser-origin requests).
 */

import { getSessionId } from './api-client';

export interface SponsorResult {
  txBytes: string;      // base64-encoded sponsored TransactionData
  signature: string;    // base64-encoded sponsor signature
  txDigest: string;     // base58-encoded digest
  expireAtTime: number; // unix timestamp
}

/**
 * Check if gas sponsorship is available.
 * Always true — handled by the Vercel function at /api/zklogin/sponsor.
 */
export function canSponsor(): boolean {
  return true;
}

/**
 * Sponsor a gasless transaction via Vercel function → Shinami Gas Station.
 * @param txKindBase64 - base64-encoded TransactionKind bytes (built with onlyTransactionKind: true)
 * @param senderAddress - the sender's Sui address
 */
export async function sponsorTransaction(
  txKindBase64: string,
  senderAddress: string
): Promise<SponsorResult> {
  const url = '/api/zklogin/sponsor';
  console.log('[shinami-gas] Requesting gas sponsorship via backend proxy...');

  // The sponsor endpoint now requires an authenticated session and only
  // sponsors the caller's own transaction — attach the session bearer.
  const sessionId = getSessionId();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (sessionId) headers['Authorization'] = `Bearer ${sessionId}`;

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ txKindBase64, senderAddress }),
  });

  if (!response.ok) {
    const text = await response.text();
    console.error('[shinami-gas] Backend proxy error:', response.status, text,
      '| sender:', senderAddress, '| txKindLen:', txKindBase64.length);
    throw new Error(`Gas sponsorship request failed (HTTP ${response.status})`);
  }

  const data = await response.json();

  if (!data.success) {
    console.error('[shinami-gas] Sponsorship error:', data.error);
    throw new Error(`Gas sponsorship failed: ${data.error || data.message || 'Unknown error'}`);
  }

  // The backend returns { success: true, ...result } where result is the Shinami response
  // Extract the SponsorResult fields
  const result: SponsorResult = {
    txBytes: data.txBytes,
    signature: data.signature,
    txDigest: data.txDigest,
    expireAtTime: data.expireAtTime,
  };

  console.log('[shinami-gas] Transaction sponsored successfully via backend proxy');
  return result;
}
